/**
 * Placeholder email — a synthetic address standing in for one that CANNOT
 * RECEIVE. Two origins: channel-first self-signups that never had an address
 * (WhatsApp phone, Instagram IGSID), and a real address retired after it
 * permanently bounced.
 *
 * When a first-time contact messages the studio on a channel that has no email,
 * the inbound resolver dispatches `member_self_signup`. `members.email` is
 * nullable, but the signup path synthesises a deterministic placeholder rather
 * than leaving it NULL:
 *   - WhatsApp:  `<sanitized-phone>@whatsapp.placeholder.layer`
 *   - Instagram: `<igsid>@instagram.placeholder.layer`
 *
 * These addresses do not deliver — every outbound email to one hard-bounces,
 * polluting Resend's suppression/deliverability metrics. Senders must skip
 * them. This module is the SINGLE SOURCE OF TRUTH for the placeholder domains
 * so every minter (`apps/webhook-receiver` inbound resolver) and guard
 * (`send_email` action) can never drift on the suffixes.
 */

/** Domain suffix of synthetic WhatsApp-signup placeholder emails. */
export const WHATSAPP_PLACEHOLDER_EMAIL_DOMAIN = "whatsapp.placeholder.layer";

/** Domain suffix of synthetic Instagram-signup placeholder emails. */
export const INSTAGRAM_PLACEHOLDER_EMAIL_DOMAIN = "instagram.placeholder.layer";

/**
 * Domain suffix for an address RETIRED after a permanent bounce (2026-09-18).
 *
 * Unlike the two above, this one does not mark a signup channel — it marks a
 * real address the provider has told us does not exist (`550 5.1.1`), swapped
 * out so nothing writes to it again. The retired address is NOT discarded: it
 * is recorded on a `session_notes` row for the member before the swap, so the
 * studio can still recognise who the person is and ask them for a new one.
 *
 * It belongs on this list because every reader already treats a placeholder as
 * "no email at all" — `member-identity-carry` says so in as many words, and
 * gives the reason as "an address that hard-bounces". A permanently-bounced
 * address IS that, so it inherits `send_email`'s skip and the `/data`, merge-
 * preview and reply-channel behaviour for free, with no new branch anywhere.
 *
 * Shape: `<member-uuid-without-hyphens>@bounced.placeholder.layer` — unique per
 * member (the column is unique per studio) and traceable back to the row.
 */
export const BOUNCED_PLACEHOLDER_EMAIL_DOMAIN = "bounced.placeholder.layer";

/** Every placeholder domain — an address here can never be delivered to. */
export const PLACEHOLDER_EMAIL_DOMAINS = [
  WHATSAPP_PLACEHOLDER_EMAIL_DOMAIN,
  INSTAGRAM_PLACEHOLDER_EMAIL_DOMAIN,
  BOUNCED_PLACEHOLDER_EMAIL_DOMAIN,
] as const;

/**
 * The retirement address for a member whose real one permanently bounced.
 * Deterministic, so re-running a cleanup is a no-op rather than a second swap.
 */
export function bouncedPlaceholderEmail(memberId: string): string {
  return `${memberId.replace(/[^A-Za-z0-9]/g, "")}@${BOUNCED_PLACEHOLDER_EMAIL_DOMAIN}`;
}

/**
 * True when `email` is a synthetic channel-signup placeholder (its domain is
 * one of {@link PLACEHOLDER_EMAIL_DOMAINS}). Case-insensitive. Callers (notably
 * `send_email`) skip delivery to these addresses.
 */
export function isPlaceholderEmail(email: string): boolean {
  const lower = email.toLowerCase();
  return PLACEHOLDER_EMAIL_DOMAINS.some((d) => lower.endsWith(`@${d}`));
}
